/**
 * Custom hooks per Documents
 * Include upload, classify, extract, validate, download
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiGet,
  apiPost,
  apiPostFormData,
  apiDelete,
  apiPatch,
  apiGetBlob,
} from '@/lib/api-client';
import { FOLDERS_QUERY_KEYS } from '@/hooks/api/use-folders';
import type {
  DocumentResponse,
  DocumentListFilters,
  ExtractionResultResponse,
  DocumentParsingResponse,
  ClassificationResultResponse,
  ExtractRequest,
  ValidationIssueResponse,
  DocumentDownloadResponse,
  ExtractionStrategyResponse,
  ExtractionStatsResponse,
} from '@/types/api/documents';

const DOCUMENTS_QUERY_KEYS = {
  all: ['documents'] as const,
  lists: () => [...DOCUMENTS_QUERY_KEYS.all, 'list'] as const,
  list: (filters?: DocumentListFilters) => [...DOCUMENTS_QUERY_KEYS.lists(), filters] as const,
  byContainer: (containerId: string) => [...DOCUMENTS_QUERY_KEYS.all, 'container', containerId] as const,
  details: () => [...DOCUMENTS_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...DOCUMENTS_QUERY_KEYS.details(), id] as const,
  extraction: (id: string) => [...DOCUMENTS_QUERY_KEYS.detail(id), 'extraction'] as const,
  parsings: (id: string) => [...DOCUMENTS_QUERY_KEYS.detail(id), 'parsings'] as const,
  validationIssues: (id: string) => [...DOCUMENTS_QUERY_KEYS.detail(id), 'validation-issues'] as const,
  download: (id: string) => [...DOCUMENTS_QUERY_KEYS.detail(id), 'download'] as const,
};

/**
 * Hook per lista documents con filtri
 */
export function useDocuments(filters?: DocumentListFilters) {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.list(filters),
    queryFn: async (): Promise<DocumentResponse[]> => {
      return await apiGet<DocumentResponse[]>('/documents', {
        params: filters as Record<string, string | number | boolean | null | undefined> | undefined
      });
    },
  });
}

/**
 * Hook per lista documents di un container specifico
 */
export function useContainerDocuments(containerId: string | undefined) {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.byContainer(containerId || ''),
    queryFn: async (): Promise<DocumentResponse[]> => {
      return await apiGet<DocumentResponse[]>('/documents', {
        params: { container_id: containerId }
      });
    },
    enabled: !!containerId,
  });
}

/**
 * Hook per singolo document
 */
export function useDocument(documentId: string) {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.detail(documentId),
    queryFn: async (): Promise<DocumentResponse> => {
      return await apiGet<DocumentResponse>(`/documents/${documentId}`);
    },
    enabled: !!documentId,
  });
}

/**
 * Hook per upload document (multipart/form-data)
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      containerId,
      documentType,
      receiverCompanyId,
      arrangementId,
      operationalRouteId,
    }: {
      file: File;
      containerId?: string;
      documentType?: string;
      receiverCompanyId?: string;
      arrangementId?: string;
      operationalRouteId?: string;
    }): Promise<DocumentResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      if (containerId) {
        formData.append('container_id', containerId);
      }
      if (documentType) {
        formData.append('document_type', documentType);
      }
      if (receiverCompanyId) {
        formData.append('receiver_company_id', receiverCompanyId);
      }
      if (arrangementId) {
        formData.append('arrangement_id', arrangementId);
      }
      if (operationalRouteId) {
        formData.append('operational_route_id', operationalRouteId);
      }

      return await apiPostFormData<DocumentResponse>('/documents/upload', formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.lists() });
    },
  });
}

/**
 * Hook per classificare document
 */
export function useClassifyDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string): Promise<ClassificationResultResponse> => {
      return await apiPost<ClassificationResultResponse>(`/documents/${documentId}/classify`);
    },
    onSuccess: (_data, documentId) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.detail(documentId) });
    },
  });
}

/**
 * Hook per estrarre dati da document
 */
export function useExtractDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, request }: { documentId: string; request?: ExtractRequest }): Promise<DocumentParsingResponse> => {
      return await apiPost<DocumentParsingResponse>(`/documents/${documentId}/extract`, request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.detail(variables.documentId) });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.extraction(variables.documentId) });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.parsings(variables.documentId) });
    },
  });
}

/**
 * Hook per risultati estrazione document
 */
export function useDocumentExtraction(documentId: string) {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.extraction(documentId),
    queryFn: async (): Promise<ExtractionResultResponse> => {
      return await apiGet<ExtractionResultResponse>(`/documents/${documentId}/extraction`);
    },
    enabled: !!documentId,
  });
}

/**
 * Hook per tutti i parsing attempts di un document
 */
export function useDocumentParsings(documentId: string) {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.parsings(documentId),
    queryFn: async (): Promise<DocumentParsingResponse[]> => {
      return await apiGet<DocumentParsingResponse[]>(`/documents/${documentId}/parsings`);
    },
    enabled: !!documentId,
  });
}

/**
 * Hook per selezionare parsing come best
 */
export function useSelectParsing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, parsingId }: { documentId: string; parsingId: string }): Promise<DocumentParsingResponse> => {
      return await apiPost<DocumentParsingResponse>(`/documents/${documentId}/parsings/${parsingId}/select`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.detail(variables.documentId) });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.extraction(variables.documentId) });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.parsings(variables.documentId) });
    },
  });
}

/**
 * Hook per validare document
 */
export function useValidateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string): Promise<DocumentResponse> => {
      return await apiPost<DocumentResponse>(`/documents/${documentId}/validate`);
    },
    onSuccess: (_data, documentId) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.detail(documentId) });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.validationIssues(documentId) });
      // Also invalidate container document lists to update sidebar status
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook per annullare validazione document
 */
export function useUnvalidateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string): Promise<DocumentResponse> => {
      return await apiPost<DocumentResponse>(`/documents/${documentId}/unvalidate`);
    },
    onSuccess: (_data, documentId) => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.detail(documentId) });
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook per validation issues di un document
 */
export function useDocumentValidationIssues(documentId: string) {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.validationIssues(documentId),
    queryFn: async (): Promise<ValidationIssueResponse[]> => {
      return await apiGet<ValidationIssueResponse[]>(`/documents/${documentId}/validation-issues`);
    },
    enabled: !!documentId,
  });
}

/**
 * Hook per pre-signed download URL
 */
export function useDocumentDownload(documentId: string, expirationHours?: number) {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEYS.download(documentId),
    queryFn: async (): Promise<DocumentDownloadResponse> => {
      const params = expirationHours ? { expiration_hours: expirationHours } : undefined;
      return await apiGet<DocumentDownloadResponse>(`/documents/${documentId}/download`, { params });
    },
    enabled: !!documentId,
    staleTime: 0, // Download URLs sono sempre fresh
  });
}

/**
 * Hook per eliminare document
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string): Promise<void> => {
      return await apiDelete<void>(`/documents/${documentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: FOLDERS_QUERY_KEYS.all });
    },
  });
}

/**
 * Hook per aggiornare document (manual classification, metadata)
 */
export interface DocumentUpdateRequest {
  document_type?: string;
  document_type_id?: string;
  document_number?: string;
  document_date?: string;
  notes?: string;
  file_name?: string;
  container_id?: string;
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, data }: { documentId: string; data: DocumentUpdateRequest }): Promise<DocumentResponse> => {
      return await apiPatch<DocumentResponse>(`/documents/${documentId}`, data);
    },
    onSuccess: (_data, variables) => {
      // Invalidate all document queries (detail, lists, byContainer)
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEYS.all });
      // Refresh folder/unmapped lists when container association changes
      if (variables.data.container_id) {
        queryClient.invalidateQueries({ queryKey: ['folders'] });
      }
    },
  });
}

/**
 * Hook per aggiornare un campo di estrazione manualmente
 */
export function useUpdateExtractionField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      documentId,
      fieldKey,
      fieldValue,
    }: {
      documentId: string;
      fieldKey: string;
      fieldValue: unknown;
    }): Promise<ExtractionResultResponse> => {
      return await apiPatch<ExtractionResultResponse>(
        `/documents/${documentId}/extraction-fields`,
        { field_key: fieldKey, field_value: fieldValue },
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: DOCUMENTS_QUERY_KEYS.extraction(variables.documentId),
      });
    },
  });
}

/**
 * Hook per lista extraction strategies disponibili
 */
export function useExtractionStrategies() {
  return useQuery({
    queryKey: ['extraction-strategies'],
    queryFn: async (): Promise<ExtractionStrategyResponse[]> => {
      return await apiGet<ExtractionStrategyResponse[]>('/extraction-strategies');
    },
    staleTime: 10 * 60 * 1000, // 10 minuti - le strategie cambiano raramente
  });
}

/**
 * Hook per statistiche estrazione
 */
export function useExtractionStats() {
  return useQuery({
    queryKey: ['extraction-stats'],
    queryFn: async (): Promise<ExtractionStatsResponse> => {
      return await apiGet<ExtractionStatsResponse>('/extraction-stats');
    },
    staleTime: 5 * 60 * 1000, // 5 minuti
  });
}

/**
 * Hook per ottenere il blob URL del documento PDF
 * Fetches the document thumbnail/content with authentication and returns a blob URL
 * Uses apiGetBlob which handles authentication and token refresh automatically
 */
export function useDocumentBlobUrl(documentId: string | undefined) {
  return useQuery({
    queryKey: ['document-blob', documentId],
    queryFn: async (): Promise<string> => {
      if (!documentId) throw new Error('No document ID provided');

      // Use apiGetBlob which handles authentication and token refresh automatically
      const blob = await apiGetBlob(`/documents/${documentId}/thumbnail`);
      return URL.createObjectURL(blob);
    },
    enabled: !!documentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes before garbage collection
  });
}

